import React from 'react';
import { Heart, Users, Zap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';

export const About = ({ onBookNow }) => {
  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-page via-card to-page">
        <div className="container mx-auto px-6 text-center">
          <h1 className="heading-1 text-6xl md:text-7xl lg:text-8xl font-black text-primary uppercase mb-6 leading-tight">
            About Us
          </h1>
          <p className="body-large text-secondary max-w-3xl mx-auto">
            A family-owned entertainment destination bringing modern experiences to our Michigan City community.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-medium rounded-2xl p-12 mb-12">
              <h2 className="heading-3 text-4xl font-black text-primary uppercase mb-6">Our Story</h2>
              <div className="space-y-6 text-secondary body-medium leading-relaxed">
                <p>
                  The Br8k Room started with a simple belief: <span className="text-primary font-semibold">everyone deserves a place to escape, connect, and create memories.</span> In a world that's always online, we wanted to build something different – a massive 7,500 square foot space where people actually come together.
                </p>
                <p>
                  We're not just another entertainment center. We're Michigan City's gathering place for families, friends, teams, and anyone looking to br8k free from the routine. Whether you're a parent planning an unforgettable birthday, a group of friends testing your skills in VR battles, or someone who just needs to smash something after a tough week – this is your space.
                </p>
                <p>
                  From cutting-edge Zero Latency premium VR with our 1,053 square foot arena and multiple 400 square foot standard free-roam VR areas, to three retro gaming consoles (each with 40,000+ games), from competitive arenas to our legendary Br8k Room – we've built something for everyone across our 7,500 square foot facility. Every corner is designed with one goal: <span className="text-blue font-semibold">bring people together and make them smile.</span>
                </p>
                <p>
                  We're proud to be locally owned and operated right here in Michigan City, Indiana. When you walk through our doors, you're not just a customer – <span className="text-pink font-semibold">you're part of our community.</span> This is where friendships are forged, where teams become closer, and where kids discover that real-world adventures beat screen time every single time.
                </p>
              </div>
            </div>

            {/* Mission & Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-olive border border-medium rounded-2xl p-8">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6">
                  <Target className="text-inverse" size={32} />
                </div>
                <h3 className="heading-4 text-primary font-bold mb-4 uppercase">Our Purpose</h3>
                <p className="text-secondary body-small leading-relaxed">
                  To create a space where people of all ages can disconnect from daily stress and reconnect with what matters: genuine fun, real connections, and unforgettable shared experiences.
                </p>
              </div>

              <div className="bg-olive border border-medium rounded-2xl p-8">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6">
                  <Heart className="text-inverse" size={32} />
                </div>
                <h3 className="heading-4 text-primary font-bold mb-4 uppercase">What Drives Us</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-secondary text-sm">
                    <span className="text-primary mt-1">•</span>
                    <span><span className="text-white font-semibold">Bringing people together</span> – Real connections beat virtual ones</span>
                  </li>
                  <li className="flex items-start gap-2 text-secondary text-sm">
                    <span className="text-primary mt-1">•</span>
                    <span><span className="text-white font-semibold">Creating joy</span> – Life's too short not to have fun</span>
                  </li>
                  <li className="flex items-start gap-2 text-secondary text-sm">
                    <span className="text-primary mt-1">•</span>
                    <span><span className="text-white font-semibold">Supporting our community</span> – We're locals serving locals</span>
                  </li>
                  <li className="flex items-start gap-2 text-secondary text-sm">
                    <span className="text-primary mt-1">•</span>
                    <span><span className="text-white font-semibold">Making it accessible</span> – Great experiences for all budgets</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* What Makes Us Different */}
            <div className="bg-page border border-medium rounded-2xl p-12">
              <h2 className="heading-3 text-4xl font-black text-primary uppercase mb-8 text-center">What Makes Us Different</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="text-inverse" size={28} />
                  </div>
                  <h3 className="heading-5 text-primary font-bold mb-3 uppercase">Premium Equipment</h3>
                  <p className="text-secondary text-sm leading-relaxed">
                    We invest in the best tech so you get the best experience. No compromises.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="text-inverse" size={28} />
                  </div>
                  <h3 className="heading-5 text-primary font-bold mb-3 uppercase">Local Ownership</h3>
                  <p className="text-secondary text-sm leading-relaxed">
                    We live here, we care about this community, and we're in this for the long haul.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="text-inverse" size={28} />
                  </div>
                  <h3 className="heading-5 text-primary font-bold mb-3 uppercase">Customer First</h3>
                  <p className="text-secondary text-sm leading-relaxed">
                    Your experience matters. We're here to make sure every visit is legendary.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-2 text-4xl md:text-5xl font-black text-primary uppercase mb-6">Come Break With Us</h2>
          <p className="body-medium text-secondary max-w-2xl mx-auto mb-8">
            Ready to experience the difference? Book your visit today.
          </p>
          <Button onClick={onBookNow} className="btn-primary text-lg px-8 py-6">
            Book Your Experience
          </Button>
        </div>
      </section>
    </div>
  );
};